/**
 * Divisional-chart (varga) significator scoring — an independent classical cross-check.
 *
 * Classical Vedic timing judges an event in the relevant DIVISIONAL chart, not only the
 * Rasi (D1): marriage in the Navamsa (D9), children in the Saptamsa (D7), career in the
 * Dasamsa (D10), education in the Chaturvimsamsa (D24). The dasha lord active at the event
 * should signify the event's houses in that divisional chart too.
 *
 * Two reasons this matters for rectification:
 *   1. Independence — the D9/D7/D10 give a separate verdict from the D1, so the rising sign
 *      converges from more than one method instead of one possibly-ambiguous chart.
 *   2. Resolution — a divisional ascendant moves far faster than the D1 (D9 changes every
 *      ~13 min, D60 every ~2 min), so these are sharply birth-time sensitive.
 *
 * Significators here are the classical pair for a varga: the occupants of the event's houses
 * plus the lords of those houses (whole-sign), with nodes acting for their dispositor. (The
 * KP four-fold star-lord scheme is a Rasi/nakshatra construct and is not applied to vargas.)
 */

import { SIGN_RULERS } from "../astro/constants";
import { get_current_dasha } from "../astro/dasha";
import { dasha_lord_at_level } from "../astro/models";
import type { RawChartData } from "../astro/models";
import { get_divisional_position } from "../astro/transit_data";
import { pymod } from "./pyutil";
import type { PlainDateTime } from "./datetime";

const _GRAHAS = ["sun", "moon", "mars", "mercury", "jupiter", "venus", "saturn", "rahu", "ketu"] as const;
const _DASHA_LEVELS = ["maha", "antar", "pratyantar", "sukshma"];
const _DASHA_LEVEL_WEIGHTS: Record<string, number> = { maha: 1.0, antar: 1.0, pratyantar: 0.6, sukshma: 0.3 };

// Event category -> the divisional chart that classically governs it (BPHS varga assignments).
// Categories not listed are judged in the Rasi (D1) only and get no divisional component.
export const CATEGORY_DIVISION: Record<string, number> = {
  marriage: 9, intimacy_relationship: 9, relationship_conflict: 9, // D9 Navamsa: spouse
  child_birth: 7,                                                  // D7 Saptamsa: progeny
  career_breakthrough: 10, career_contract: 10, career_failure: 10, // D10 Dasamsa: status
  career_learning: 10, award: 10,
  education_start: 24,                                             // D24: learning
  property_purchase: 4,                                            // D4 Chaturthamsa: home/property/fixed assets
  relocation: 4,                                                   // D4 Chaturthamsa: home/property/fixed assets
  death_family: 12,                                                // D12 Dwadasamsa: parents/lineage
  injury: 30, anxiety_health: 30, mood_low: 30, pet_death: 30,     // D30 Trimsamsa: affliction/disease
};

export function category_division(category: string): number | null {
  return CATEGORY_DIVISION[String(category || "").trim().toLowerCase()] ?? null;
}

/**
 * Grahas that signify the given houses in the varga: occupants of those houses plus
 * the lords of those houses (whole-sign from the varga ascendant). Nodes also signify if
 * their varga dispositor does.
 */
export function divisional_significators(
  natal_chart: RawChartData,
  division: number,
  houses: number[],
): Set<string> {
  const asc_sign = get_divisional_position(natal_chart.ascendant_longitude, division)[0];
  const planet_sign: Record<string, number> = {};
  for (const name of _GRAHAS) {
    const pos = natal_chart.planets[name];
    if (pos !== undefined) {
      planet_sign[name] = get_divisional_position(pos.longitude, division)[0];
    }
  }
  const house_signs = new Set(houses.map((h) => pymod(asc_sign + (h - 1), 12)));
  const sig = new Set<string>();
  for (const [planet, sign] of Object.entries(planet_sign)) {
    if (house_signs.has(sign)) {
      sig.add(planet);
    }
  }
  for (const sign of house_signs) {
    sig.add(SIGN_RULERS[sign]);
  }
  for (const node of ["rahu", "ketu"]) {
    if (node in planet_sign && sig.has(SIGN_RULERS[planet_sign[node]])) {
      sig.add(node);
    }
  }
  return sig;
}

/**
 * How well the dasha chain active at the event date signifies the event's houses in the
 * relevant divisional chart. Returns (0-100 weighted fraction of the chain that signifies
 * them, or None, detail).
 */
export function score_divisional_for_event(
  natal_chart: RawChartData,
  division: number,
  houses: number[],
  event_dt: PlainDateTime,
  birth_dt: PlainDateTime,
  dasha_info_init: Record<string, number>,
  timezone_offset: number,
): [number | null, Record<string, any>] {
  const sig = divisional_significators(natal_chart, division, houses);
  let dasha;
  try {
    dasha = get_current_dasha(event_dt, birth_dt, dasha_info_init, timezone_offset);
  } catch {
    return [null, { error: "dasha_error" }];
  }
  let total = 0.0;
  let hit = 0.0;
  const levels: Record<string, any> = {};
  for (const level of _DASHA_LEVELS) {
    const lord = dasha_lord_at_level(dasha, level);
    if (lord === null || lord === undefined) {
      continue;
    }
    const weight = _DASHA_LEVEL_WEIGHTS[level];
    total += weight;
    const matched = sig.has(lord);
    if (matched) {
      hit += weight;
    }
    levels[level] = { lord: lord, signifies: matched };
  }
  if (total <= 0) {
    return [null, { division: division, significators: [...sig].sort() }];
  }
  return [
    (100.0 * hit) / total,
    { division: division, significators: [...sig].sort(), levels: levels },
  ];
}
